import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

const DISMISS_KEY = "wc.installPromptDismissed";

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosGuide, setIosGuide] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed) return;

    // Standalone means already installed
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    // Android / Chrome
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari: show manual instructions after 4s if no event
    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isIos && isSafari) {
      const t = setTimeout(() => {
        setIosGuide(true);
        setVisible(true);
      }, 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", handler);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice;
      dismiss();
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-24 z-[60] flex justify-center px-4 pointer-events-none">
      <div
        className="pointer-events-auto max-w-md w-full bg-[#0A3D62] text-[#FAF8F3] rounded-2xl p-4 shadow-2xl flex items-start gap-3"
        data-testid="install-prompt"
      >
        <div className="text-3xl">🎣</div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-black text-base">Pin it to your home screen</div>
          <div className="text-xs opacity-80 mt-0.5">
            {iosGuide
              ? "Tap Share → Add to Home Screen. Opens instantly, works dam-side."
              : "Install the Glenlyon Fishing Challenge for quick, offline-friendly access."}
          </div>
          <div className="flex gap-2 mt-2">
            {deferred && !iosGuide && (
              <button
                onClick={install}
                data-testid="install-prompt-btn"
                className="px-3 py-1.5 rounded-lg bg-[#E8A838] text-[#1A2A35] font-bold text-sm flex items-center gap-1"
              >
                <Download className="w-3.5 h-3.5" strokeWidth={2.5} /> Install
              </button>
            )}
            <button
              onClick={dismiss}
              data-testid="install-prompt-dismiss"
              className="px-3 py-1.5 rounded-lg bg-white/10 text-[#FAF8F3] font-bold text-sm"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0"
          aria-label="Close"
        >
          <X className="w-4 h-4" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}
